import moment from 'moment';

import {getTimezoneAbbreviation} from 'nutshell-core/date-time';

import type {StatusBarProps} from 'shells/control-bar/status-bar';
import {StatusBarPrimaryText} from 'shells/control-bar/status-bar-primary-text';
import {
    getEditionStatusInfo,
    getDripEditionStatusInfo,
    getTemplateEditionStatusInfo,
} from 'shells/edition/get-edition-status-info';
import type {StatusBadgeProps} from 'shells/status-badge/status-badge';
import {FormattedTimestamp} from 'shells/timestamp/formatted-timestamp';
import {RelativeTimestamp} from 'shells/timestamp/relative-timestamp';
import {UserActionTimestamp} from 'shells/timestamp/user-action-timestamp';

import type {CampaignType} from 'nutshell-graphql-types';
import type {
    EditionFragment as Edition,
    ComparisonEditionFragment,
    SmsEditionFragment as SmsEdition,
    SmsDripEditionFragment as SmsDripEdition,
} from 'nutshell-graphql-types';

export function getEditionStatusBarInfo(
    edition: Edition | SmsEdition | SmsDripEdition,
    canAccessFullCampaigns: boolean,
    onSchedule: (() => void) | null | undefined,
    isSaving: boolean,
    isOwner: boolean
): StatusBarProps {
    const campaignType = edition.campaign.type;

    return {
        status: getEditionStatusProps(edition),
        primaryText: getEditionStatusTitleComponent(
            edition,
            campaignType,
            canAccessFullCampaigns,
            onSchedule,
            isOwner
        ),
        secondaryText: getEditionTimestampComponent(edition, campaignType, isSaving),
    };
}

function getEditionStatusProps(
    edition: Edition | SmsEdition | SmsDripEdition
): StatusBadgeProps | null | undefined {
    const campaignType = edition.campaign.type;

    if (campaignType === 'DRIP_SEQUENCE') {
        return getDripEditionStatusInfo(edition.status);
    } else if (campaignType === 'TEMPLATE') {
        if (edition.campaign.deletedTime) {
            // Templates base Archived status on whether deletedTime exists, so this must be manually checked
            return {level: 'hidden', text: 'Archived'};
        } else {
            return getTemplateEditionStatusInfo(edition.status);
        }
    } else {
        return edition.deletedTime
            ? {level: 'hidden', text: 'Archived'}
            : getEditionStatusInfo(edition.status);
    }
}

function getEditionStatusTitleComponent(
    edition: Edition | SmsEdition | SmsDripEdition,
    campaignType: CampaignType,
    canAccessFullCampaigns: boolean,
    onSchedule: (() => void) | null | undefined,
    isOwner: boolean
) {
    // Newsletters and broadcasts can be scheduled, but archived SMS broadcasts should not show the link
    if (campaignType !== 'DRIP_SEQUENCE') {
        return (
            <StatusBarPrimaryText
                text={getScheduledEditionStatusText(edition, campaignType, canAccessFullCampaigns)}
                linkProps={
                    (edition.status === 'SCHEDULED' || edition.status === 'NEW') &&
                    onSchedule &&
                    (canAccessFullCampaigns || isOwner) &&
                    campaignType !== 'TEMPLATE' &&
                    !edition.deletedTime
                        ? {
                              onClick: onSchedule,
                              linkText:
                                  edition.status === 'SCHEDULED' ? 'Reschedule' : 'Schedule now',
                          }
                        : undefined
                }
            />
        );
    }

    // Drip sequences always show number of recipients unless the message is a draft
    return (
        <StatusBarPrimaryText
            text={
                edition.status === 'NEW'
                    ? 'You haven’t started sending this yet'
                    : getEditionRecipientText(edition)
            }
        />
    );
}

function getScheduledEditionStatusText(
    edition: Edition | SmsEdition | SmsDripEdition,
    campaignType: CampaignType,
    canAccessFullCampaigns: boolean
) {
    if (campaignType === 'TEMPLATE') {
        if (edition.deletedTime) {
            return 'This template is currently archived';
        } else if (edition.status === 'USER_TEMPLATE_PUBLISHED') {
            return 'This template is available for your team to use';
        } else if (edition.status === 'NEW') {
            return 'Publish this template to make it available for all users';
        } else {
            return 'You’re editing a template which cannot be sent';
        }
    }

    const isNewsletter = campaignType === 'NEWSLETTER' || campaignType === 'AI_STARTER_TEMPLATE';

    if (edition.status === 'SENT') {
        return getEditionRecipientText(edition);
    }

    let timezone = '';
    // @ts-expect-error
    if (edition.scheduledTime && typeof edition.scheduledTime === 'number') {
        // @ts-expect-error
        timezone = getTimezoneAbbreviation(edition.scheduledTime);
    }

    if (
        edition.status === 'SCHEDULED' &&
        // @ts-expect-error
        edition.scheduledTime &&
        // @ts-expect-error
        typeof edition.scheduledTime === 'number' &&
        timezone
    ) {
        return `Scheduled to send on ${moment
            // @ts-expect-error
            .unix(edition.scheduledTime)
            .format(FormattedTimestamp.formats.longDateWithTime)} ${timezone}`;
    } else if (
        edition.status === 'SENDING' ||
        edition.status === 'COMPARISON_SENDING' ||
        edition.status === 'COMPARISON_WAITING'
    ) {
        // @ts-expect-error
        if (edition.isABTesting && edition.status === 'SENDING') {
            const versionAIsWinner =
                // @ts-expect-error
                edition.comparison &&
                // @ts-expect-error
                edition.comparison.winnerEdition &&
                // @ts-expect-error
                edition.comparison.winnerEdition.id
                    ? // @ts-expect-error
                      edition.comparison.winnerEdition.id === edition.id
                    : false;

            return versionAIsWinner
                ? 'Version A is sending to remaining recipients'
                : 'Version B is sending to remaining recipients';
        }

        return 'Currently sending!';
    } else if (edition.status === 'HALTED') {
        return `This ${
            isNewsletter ? 'edition' : 'broadcast'
        } was halted due to its high bounce rate`;
    } else if (edition.status === 'IN_REVIEW') {
        return `This ${isNewsletter ? 'edition' : 'broadcast'} is under review before sending`;
    } else {
        return canAccessFullCampaigns
            ? `This ${isNewsletter ? 'edition' : 'broadcast'} isn’t scheduled to send`
            : `This ${isNewsletter ? 'edition' : 'broadcast'} has not been sent`;
    }
}

function getEditionTimestampComponent(
    edition: Edition | SmsEdition | SmsDripEdition,
    campaignType: CampaignType,
    isSaving: boolean
) {
    if (isSaving) {
        return <span>Saving…</span>;
    }

    let sendTime;
    // @ts-expect-error
    if (edition.isABTesting && edition.comparison && !edition.comparison.winnerEdition) {
        // @ts-expect-error
        sendTime = (edition.comparison && edition.comparison.startTime) || null;
    } else {
        // @ts-expect-error
        sendTime = edition.sendTime || null;
    }

    if (campaignType !== 'DRIP_SEQUENCE') {
        if (
            (edition.status === 'SENDING' ||
                edition.status === 'COMPARISON_SENDING' ||
                edition.status === 'COMPARISON_WAITING') &&
            sendTime &&
            typeof sendTime === 'number'
        ) {
            return (
                <span className='flex'>
                    Started sending&nbsp;
                    <RelativeTimestamp datetime={sendTime} format='fromNow' />
                </span>
            );
        }

        if (
            edition.status === 'SENT' &&
            // @ts-expect-error
            edition.scheduledTime &&
            // @ts-expect-error
            typeof edition.scheduledTime === 'number'
        ) {
            return (
                <span className='flex'>
                    Delivered&nbsp;
                    <FormattedTimestamp
                        // @ts-expect-error
                        datetime={edition.scheduledTime}
                        format={FormattedTimestamp.formats.longDateWithTime}
                    />
                </span>
            );
        }
    } else if (edition.status !== 'NEW') {
        // @ts-expect-error
        return edition.lastSentTime && typeof edition.lastSentTime === 'number' ? (
            <span className='flex'>
                Last sent&nbsp;{' '}
                <FormattedTimestamp
                    // @ts-expect-error
                    datetime={edition.lastSentTime}
                    format={FormattedTimestamp.formats.longDateWithTime}
                />
            </span>
        ) : (
            <span>This edition hasn’t been sent yet.</span>
        );
    }

    const baseEditionModifiedTime =
        edition.__typename === 'Edition'
            ? getLastModifiedTimeForEdition(edition)
            : edition.modifiedTime;
    if (edition.__typename === 'Edition' && edition.comparisonEdition) {
        const comparisonEditionModifiedTime = getLastModifiedTimeForEdition(
            edition.comparisonEdition
        );

        if (baseEditionModifiedTime && comparisonEditionModifiedTime) {
            const modifiedTimeToUse = Math.max(
                baseEditionModifiedTime,
                comparisonEditionModifiedTime
            );

            return (
                <UserActionTimestamp
                    format='relative'
                    user={null}
                    modifiedTime={modifiedTimeToUse}
                />
            );
        }
    }

    if (baseEditionModifiedTime) {
        return (
            <UserActionTimestamp
                format='relative'
                user={null}
                modifiedTime={baseEditionModifiedTime}
            />
        );
    }
}

function getEditionRecipientText(edition: Edition | SmsEdition | SmsDripEdition): string {
    let sentCount = edition.engagementStats.totalSentCount;
    // If we're showing a comparison, gotta total the sent counts
    if (edition.__typename === 'Edition' && edition.comparison) {
        sentCount = edition.comparison.engagementStats.totalSentCount;
    }

    return `Sent to ${sentCount.toLocaleString()} ${sentCount === 1 ? 'recipient' : 'recipients'}`;
}

function getLastModifiedTimeForEdition(
    edition: Edition | ComparisonEditionFragment
): number | null | undefined {
    const editionModifiedTime: number | null | undefined = edition.modifiedTime;
    const emailTemplateModifiedTime: number | null | undefined = edition.emailTemplate
        ? edition.emailTemplate.modifiedTime
        : null;

    if (editionModifiedTime || emailTemplateModifiedTime) {
        return editionModifiedTime && emailTemplateModifiedTime
            ? Math.max(editionModifiedTime, emailTemplateModifiedTime)
            : editionModifiedTime || emailTemplateModifiedTime;
    }

    return null;
}
